/**
 * Utilities for working with the `/api/future-tips` response.
 *
 * NOTE: The backend payload shape has varied over time, so these utilities
 * defensively search for `{ tracks: [...] }` and tolerate a few race field names.
 */

const normalizeWhitespace = (value) => `${value || ""}`.replace(/\s+/g, " ").trim();

export const normalizeTrackNameForLookup = (trackName) =>
  normalizeWhitespace(`${trackName || ""}`.replace(/-/g, " "))
    .toLowerCase();

const normalizeHorseName = (name) =>
  normalizeWhitespace(name).toLowerCase();

const parseHorseNumberFromFormattedName = (formattedName) => {
  if (typeof formattedName !== "string") return "";
  const trimmed = formattedName.trim();
  if (!trimmed) return "";
  const dotIdx = trimmed.indexOf(".");
  const candidate = dotIdx >= 0 ? trimmed.slice(0, dotIdx) : trimmed;
  return candidate.trim().replace(/^0+/, "") || candidate.trim();
};

const parseHorseNameFromFormattedName = (formattedName) => {
  if (typeof formattedName !== "string") return "";
  const trimmed = formattedName.trim();
  if (!trimmed) return "";
  const dotIdx = trimmed.indexOf(".");
  if (dotIdx < 0) return trimmed;
  return trimmed.slice(dotIdx + 1).trim();
};

const parseOdds = (value) => {
  const n = typeof value === "number" ? value : parseFloat(String(value).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Extract `{ tracks: [...] }` payload from a nested/wrapped response.
 */
export function extractFutureTipsPayload(responseJson) {
  const seen = new Set();

  const looksLikeTracksPayload = (obj) => {
    if (!obj || typeof obj !== "object") return false;
    if (!Array.isArray(obj.tracks)) return false;
    const first = obj.tracks[0];
    if (!first || typeof first !== "object") return false;
    if (Array.isArray(first.races)) return true;
    if (typeof first.trackName === "string" || typeof first.track === "string") return true;
    return false;
  };

  const walk = (value, depth = 0) => {
    if (!value || typeof value !== "object") return null;
    if (seen.has(value)) return null;
    seen.add(value);
    if (depth > 7) return null;

    if (looksLikeTracksPayload(value)) return value;
    if (value.data) return walk(value.data, depth + 1);
    if (value.result) return walk(value.result, depth + 1);
    if (value.payload) return walk(value.payload, depth + 1);

    for (const v of Object.values(value)) {
      const found = walk(v, depth + 1);
      if (found) return found;
    }

    return null;
  };

  return walk(responseJson) || {};
}

/**
 * Build a lookup of implied odds from several possible shapes:
 * - array of strings like "4. Horse Name: 3.25"
 * - array of objects like { selectionId, impliedOdds } or { number, odds } etc.
 * - object map like { "4": 3.25, ... }
 */
function buildImpliedOddsLookup(impliedOdds) {
  const byNumber = new Map();
  const byName = new Map();
  const bySelectionId = new Map();

  if (!impliedOdds) return { byNumber, byName, bySelectionId };

  if (Array.isArray(impliedOdds)) {
    impliedOdds.forEach((entry) => {
      if (!entry) return;

      if (typeof entry === "string") {
        const parts = entry.split(":");
        if (parts.length < 2) return;

        const left = parts[0].trim(); // "4. Horse"
        const right = parts.slice(1).join(":").trim();
        const price = parseOdds(right);
        if (!price) return;

        const dotIdx = left.indexOf(".");
        const number = dotIdx >= 0 ? left.slice(0, dotIdx).trim() : "";
        const name = dotIdx >= 0 ? left.slice(dotIdx + 1).trim() : left.trim();

        if (number) byNumber.set(number, price);
        if (name) byName.set(normalizeHorseName(name), price);
        return;
      }

      if (typeof entry === "object") {
        const selectionId = entry.selectionId ?? entry.selectionID ?? entry.sid ?? null;
        const number = entry.number ?? entry.horseNumber ?? entry.runnerNumber ?? null;
        const name = entry.name ?? entry.runnerName ?? entry.selectionName ?? null;
        const price =
          parseOdds(entry.impliedOdds) ??
          parseOdds(entry.implied_odds) ??
          parseOdds(entry.odds) ??
          parseOdds(entry.price) ??
          parseOdds(entry.winPrice);

        if (!price) return;
        if (selectionId) bySelectionId.set(String(selectionId), price);
        if (number) byNumber.set(String(number).replace(/^0+/, "") || String(number), price);
        if (name) byName.set(normalizeHorseName(name), price);
      }
    });

    return { byNumber, byName, bySelectionId };
  }

  if (typeof impliedOdds === "object") {
    Object.entries(impliedOdds).forEach(([key, val]) => {
      const price = parseOdds(val);
      if (!price) return;
      const cleanedKey = String(key).trim();
      if (!cleanedKey) return;
      byNumber.set(cleanedKey.replace(/^0+/, "") || cleanedKey, price);
    });
  }

  return { byNumber, byName, bySelectionId };
}

function enrichFirstFourDataWithImpliedOdds(firstFourData, impliedOdds) {
  if (!Array.isArray(firstFourData) || firstFourData.length === 0) return [];
  const lookup = buildImpliedOddsLookup(impliedOdds);

  return firstFourData.map((tip) => {
    if (!tip || typeof tip !== "object") return tip;

    const selectionId = tip.selectionId ? String(tip.selectionId) : null;
    const formattedName = tip.formattedName || tip.selectionName || tip.runnerName || tip.name || "";

    const number =
      `${tip.number ?? ""}`.trim() ||
      parseHorseNumberFromFormattedName(formattedName);

    const name =
      normalizeHorseName(tip.name || parseHorseNameFromFormattedName(formattedName) || "");

    const price =
      (selectionId && lookup.bySelectionId.get(selectionId)) ||
      (number && lookup.byNumber.get(number)) ||
      (name && lookup.byName.get(name)) ||
      null;

    // ResultsArchiveTable already displays odds using `lastPriceTraded || currentPrice`,
    // so set `currentPrice` to implied odds when present.
    return price ? { ...tip, currentPrice: price } : tip;
  });
}

/**
 * Returns a Map keyed by `${normalizedTrack}|${raceNumber}` → array of tips (firstFourData),
 * where each tip has `currentPrice` set from `impliedOdds` when available.
 */
export function buildEarlyMailTipsLookupFromFutureTipsResponse(responseJson) {
  const payload = extractFutureTipsPayload(responseJson);
  const tracks = Array.isArray(payload?.tracks) ? payload.tracks : [];
  const lookup = new Map();

  tracks.forEach((track) => {
    const rawTrackName = track?.trackName || track?.track || track?.name || "";
    const trackKey = normalizeTrackNameForLookup(rawTrackName);
    if (!trackKey) return;

    const races = Array.isArray(track?.races) ? track.races : [];
    races.forEach((race) => {
      const raceNumberRaw = race?.race ?? race?.raceNumber ?? race?.number ?? race?.id ?? "";
      const raceNumber = String(raceNumberRaw).trim();
      if (!raceNumber) return;

      const firstFourData =
        race?.firstFourData ||
        race?.predictionData?.firstFourData ||
        race?.data?.firstFourData ||
        [];

      const enriched = enrichFirstFourDataWithImpliedOdds(
        Array.isArray(firstFourData) ? firstFourData : [],
        race?.impliedOdds ?? race?.implied_odds ?? race?.odds ?? null,
      );

      if (!enriched.length) return;
      lookup.set(`${trackKey}|${raceNumber}`, enriched);
    });
  });

  return lookup;
}

